import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getStatusColor } from '../utils/statusUtils';
import { statusLabel, subStatusLabel } from '../utils/webcheckinStatus';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  styled
} from '@mui/material';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

/**
 * Business4Flyo Check-in Table component
 * @param {Object} props - Component props
 * @returns {JSX.Element} Business4FlyoCheckInTable component
 */
const Business4FlyoCheckInTable = ({
  checkIns,
  loading,
  error,
  pagination,
  onPageChange,
  onRowsPerPageChange
}) => {
  const navigate = useNavigate();

  const handleRowClick = (checkIn) => {
    console.log('Row clicked:', checkIn);
    // Use ticketId for navigation
    if (checkIn.ticketId) {
      console.log('Navigating to ticketId:', checkIn.ticketId);
      // Pass source tab as query parameter to track where user came from
      navigate(`/business4flyo/check-in/${checkIn.ticketId}?source=business4FlyoWebCheckIn`);
    } else {
      console.warn('No ticketId found in checkIn:', checkIn);
    }
  };
  // Format date and time separately
  const formatDateTime = (dateString, timeString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    
    try {
      // Format date as DD/MM/YYYY
      const [year, month, day] = dateString.split('-');
      const formattedDate = `${day}/${month}/${year}`;

      // Format time as HH:MM (24-hour format)
      const formattedTime = timeString || '00:00';

      return {
        date: formattedDate,
        time: formattedTime
      };
    } catch (error) {
      console.error('Error formatting date/time:', error);
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  // Get status chip props
  const getStatusChipProps = (status) => ({ color: getStatusColor(status).color, label: statusLabel(status) });

  const formatStatusLabel = (status) => {
    if (!status) return 'N/A';

    return status
      .toString()
      .trim()
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getBookingStatusChipProps = (status) => {
    const statusLower = status?.toString().trim().toLowerCase() || '';
    switch (statusLower) {
      case 'confirmed':
        return { color: 'success', label: 'Confirmed' };
      case 'amended':
        return { color: 'info', label: 'Amended' };
      case 'pending':
        return { color: 'warning', label: 'Pending' };
      case 'cancel':
      case 'cancelled':
        return { color: 'error', label: 'Cancelled' };
      case 'refunded':
        return { color: 'secondary', label: 'Refunded' };
      case 'in_complete':
      case 'incomplete':
        return { color: 'default', label: 'Incomplete' };
      default:
        return { color: 'default', label: formatStatusLabel(status) };
    }
  };

  // Get opt-in by color
  const getOptInByColor = (optInBy) => {
    const optInLower = optInBy?.toLowerCase() || '';
    switch (optInLower) {
      case 'client':
        return 'primary'; // Blue
      case 'agent':
        return 'success'; // Green
      case 'internal':
        return 'warning'; // Orange
      default:
        return 'default'; // Grey
    }
  };

  // Format boarding pass from array like ["0/1"]
  const formatBoardingPass = (boardingPassArray) => {
    if (!boardingPassArray || !Array.isArray(boardingPassArray) || boardingPassArray.length === 0) {
      return null;
    }
    return boardingPassArray.join(', ');
  };

  return (
    <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
      <TableContainer
        sx={{
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 250px)',
          overflow: 'auto'
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="Business4Flyo check-in table"
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              padding: '6px 8px',
              fontSize: '0.75rem'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '60px'
                }}
              >
                ID
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '150px'
                }}
              >
                Agent Name
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '80px'
                }}
              >
                Airline
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100px'
                }}
              >
                Sector
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100px'
                }}
              >
                Departure
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100px'
                }}
              >
                Scheduled
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100px'
                }}
              >
                Opt-In By
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '150px'
                }}
              >
                Check-In Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1 }}>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', py: 1 }}>
                    {error}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : checkIns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1 }}>
                    No check-ins found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              checkIns.map((checkIn) => {
                const departureDateTime = formatDateTime(checkIn.departureDate, checkIn.departureTime);
                const scheduledDateTime = formatDateTime(checkIn.scheduledDate, checkIn.scheduledTime);
                const statusProps = getStatusChipProps(checkIn.checkInStatus);
                const bookingStatusProps = getBookingStatusChipProps(checkIn.bookingStatus || checkIn.ticketStatus);
                const bookingStatusTextColor = bookingStatusProps.color === 'default'
                  ? 'text.secondary'
                  : `${bookingStatusProps.color}.main`;
                const optInByColor = getOptInByColor(checkIn.optInBy);
                const boardingPass = formatBoardingPass(checkIn.boardingPass);

                return (
                  <StyledTableRow 
                    key={checkIn.checkinAttemptId || checkIn.id}
                    onClick={() => handleRowClick(checkIn)}
                  >
                    <TableCell>{checkIn.checkinAttemptId || checkIn.id}</TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.agentName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.airline || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.sector || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.7rem' }}>
                          {departureDateTime.date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                          {departureDateTime.time}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.7rem' }}>
                          {scheduledDateTime.date || ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                          {scheduledDateTime.time || ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                        <Chip
                          size="small"
                          label={checkIn.optInBy || 'N/A'}
                          color={optInByColor}
                          sx={{
                            height: '20px',
                            '& .MuiChip-label': {
                              fontSize: '0.65rem',
                              padding: '0 6px'
                            }
                          }}
                        />
                        <Tooltip title={`Booking Status: ${bookingStatusProps.label}`}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              maxWidth: '100%',
                              fontSize: '0.65rem',
                              color: bookingStatusTextColor,
                              fontWeight: 600
                            }}
                          >
                            Booking: {bookingStatusProps.label}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusProps.label}
                        color={statusProps.color}
                        sx={{
                          height: '20px',
                          '& .MuiChip-label': {
                            fontSize: '0.65rem',
                            padding: '0 6px'
                          }
                        }}
                      />
                      {/* Show boarding pass, substatus, and message exactly like B2B check-in */}
                      {checkIn.checkInSubStatus && checkIn.checkInSubStatus !== 'NONE' && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.25, fontSize: '0.65rem', color: 'primary.main', fontWeight: 500 }}>
                          {subStatusLabel(checkIn.checkInSubStatus)}
                        </Typography>
                      )}
                      {checkIn.checkInStatus?.toLowerCase() === 'completed' && boardingPass ? (
                        <Typography variant="caption" display="block" sx={{ mt: 0.25, fontSize: '0.65rem', color: 'primary.main', fontWeight: 500 }}>
                          Boarding Pass: {boardingPass}
                        </Typography>
                      ) : (
                        <>
                          {checkIn.checkInMessage && (
                            <Tooltip title={checkIn.checkInMessage}>
                              <Typography variant="caption" display="block" sx={{ mt: 0.25, fontSize: '0.65rem' }}>
                                {checkIn.checkInMessage.length > 15
                                  ? `${checkIn.checkInMessage.substring(0, 15)}...`
                                  : checkIn.checkInMessage}
                              </Typography>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.totalItems || checkIns.length}
        page={(pagination.pageNumber || 1) - 1}
        onPageChange={(e, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={pagination.itemsPerPage || 25}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onRowsPerPageChange={(e) => {
          const newItemsPerPage = parseInt(e.target.value, 10);
          onRowsPerPageChange(newItemsPerPage);
        }}
        sx={{
          '.MuiTablePagination-toolbar': {
            minHeight: '40px',
            pl: 1
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: '0.75rem'
          },
          '.MuiTablePagination-select': {
            fontSize: '0.75rem'
          }
        }}
      />
    </Paper>
  );
};

export default Business4FlyoCheckInTable;
